import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { Formik, Form } from 'formik';
import { compareVersions } from 'compare-versions';
import {
  useTranslator,
  useAppContext,
  useToast,
  callNativeMobileApp,
  withMobileAppMode,
  mealPlaceholderImage,
  cloudflareImage,
  getCountry,
  formatCalories,
  formatDecimal,
  FormikInput as Input,
} from '@front-activerse/common-components';
import { Container, Row, Col, Button, Table, Spinner } from 'reactstrap';
import Layout from '../../Layout/Layout';
import TitleBar from '../../Layout/TitleBar';
import userMealValidationSchema from './validationSchema';
import createValidationSchema from '../../Tracker/components/mealValidationSchema';
import { useNavigate, useLocation, useParams, useOutletContext } from 'react-router-dom';
import { TrackerAddMealProps, Ingredient } from '../../Tracker/types';
import {
  buildIngredientFormValues,
  calculateUpdatedIngredients,
} from '../../Tracker/helpers/ingredientEditHelpers';
import { calculateMacrosFromIngredients } from '../../Tracker/helpers/mealCalculations';
import { toRequestIngredient } from '../../Tracker/helpers/requestIngredient';
import TrackerEditIngredientsForm from '../../Tracker/components/TrackerEditIngredientsForm';
import { TrackerMealNameModal, TrackerMealRemoveModal } from '../../Tracker/components/TrackerMeal';
import FriscoButton from '../../MealPlan/MealView/components/FriscoButton';

interface AddIngredientReturnState {
  returnPath?: string;
  ingredients?: Ingredient[];
  mealName?: string;
  servings?: number;
  updatedAt?: number;
}

// const ADD_INGREDIENT_RETURN_STORAGE_KEY = 'tracker_add_ingredient_return_state';
const ADD_INGREDIENT_RETURN_EVENT = 'tracker_add_ingredient_return';

const TrackerAddMeal: React.FC<TrackerAddMealProps> = ({
  mealData,
  addMeal,
  addUserMeal,
  editMeal,
  removeMeal,
  saveAsUserMeal,
  mobileAppMode,
  date: dateProp,
  requestId,
  dietMealId,
  mobileApp,
}) => {
  const { trackFirebaseEvent } = useOutletContext<{
    trackFirebaseEvent: (eventName: string, eventPayload: object) => void;
  }>();
  const { t, language } = useTranslator();
  const { toastError, toastSuccess } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const lastAppliedUpdateRef = useRef<number | null>(null);
  /** Latest form values – used when deleting an ingredient so we sync edits into ingredients before recalc. */
  const formValuesRef = useRef<Record<string, unknown>>({});
  /** Last valid servings value - used to calculate ratio when servings change */
  const lastValidServingsRef = useRef<number>(mealData.portions);

  const isEditMode = Boolean(editMeal);
  const [showNotes, setShowNotes] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ingredients, setIngredients] = useState<Ingredient[]>(mealData.ingredients);
  const [mealName, setMealName] = useState(mealData.name);
  const [showMealNameModal, setShowMealNameModal] = useState(false);
  const [lastIngredientsCount, setLastIngredientsCount] = useState(mealData.ingredients.length);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [hasIngredientsChanged, setHasIngredientsChanged] = useState(false);
  const [hasMealNameChanged, setHasMealNameChanged] = useState(false);
  const [preloadInstacart, setPreloadInstacart] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  /** Servings restored from add-ingredient return; when set, used in initialValues instead of mealData.portions. */
  const [currentServings, setCurrentServings] = useState<number | null>(null);
  /** Meal name restored from add-ingredient return; when set, used in initialValues instead of mealData.name. */
  const [currentMealName, setCurrentMealName] = useState<string | null>(null);

  const applyReturnState = useCallback(
    (state: AddIngredientReturnState | null) => {
      if (!state) {
        return;
      }
      // 1. Check if the return path is the same as the current path
      if (state.returnPath && state.returnPath !== location.pathname) {
        return;
      }
      // 2. protection against applying the same state again
      if (state.updatedAt != null && lastAppliedUpdateRef.current === state.updatedAt) {
        return;
      }
      // 3. update ingredients list + helper flags
      if (state.ingredients != null) {
        setIngredients(state.ingredients);
        if (isEditMode) {
          setHasIngredientsChanged(true);
        }
      }
      // 4. update the meal name if it comes in the package (state needed for enableReinitialize)
      if (state.mealName != null) {
        setCurrentMealName(state.mealName);
        setMealName(state.mealName);
      }
      // 5. restore servings so Formik reinit (from new ingredients) keeps user's value
      if (state.servings != null) {
        setCurrentServings(state.servings);
      }
      // 6. update the last applied update timestamp
      if (state.updatedAt != null) {
        lastAppliedUpdateRef.current = state.updatedAt;
      } else {
        lastAppliedUpdateRef.current = Date.now();
      }
    },
    [isEditMode, location.pathname]
  );

  // Handle return from add-ingredient: read sessionStorage on remount, and listen for custom event when component stays mounted (navigate(-1) cannot pass state).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent<AddIngredientReturnState>;
      applyReturnState(customEvent.detail ?? null);
    };
    window.addEventListener(ADD_INGREDIENT_RETURN_EVENT, handler as EventListener);
    return () => {
      window.removeEventListener(ADD_INGREDIENT_RETURN_EVENT, handler as EventListener);
    };
  }, [applyReturnState]);

  // Auto-resize textarea for notes field
  useEffect(() => {
    if (!showNotes) return;

    const textarea = textareaRef.current;
    if (!textarea) return;

    const adjustTextareaHeight = () => {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';
      // Set height based on scrollHeight, with min height
      const minHeight = 60; // Minimum height in pixels
      textarea.style.height = `${Math.max(textarea.scrollHeight, minHeight)}px`;
    };

    // Initial adjustment
    requestAnimationFrame(() => {
      adjustTextareaHeight();
    });

    // Add input event listener directly to textarea
    textarea.addEventListener('input', adjustTextareaHeight);
    textarea.addEventListener('change', adjustTextareaHeight);

    // Also adjust when window is resized
    window.addEventListener('resize', adjustTextareaHeight);

    return () => {
      textarea.removeEventListener('input', adjustTextareaHeight);
      textarea.removeEventListener('change', adjustTextareaHeight);
      window.removeEventListener('resize', adjustTextareaHeight);
    };
  }, [showNotes]);

  // Focus textarea when notes section is opened
  useEffect(() => {
    if (showNotes) {
      // Use setTimeout to ensure textarea is rendered before focusing
      const timer = setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showNotes]);

  const decimalSeparator = language === 'pl-PL' ? ',' : '.';

  const { date: dateFromParams } = useParams();
  const date = dateProp ?? dateFromParams;

  const isBarcode =
    typeof window !== 'undefined' && window.location.search.includes('isBarcode=true');

  const {
    consts: { APP_BRAND, FRISCO, INSTACART_FIREBASE_FUNCTION, INSTACART_URL },
    images: { editNeutralIcon, yourMealsRemoveIcon, addToTrackerIcon, instacartLogo, addPlusIcon },
  } = useAppContext();

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const minDateObj = new Date(today);
  minDateObj.setDate(minDateObj.getDate() - 30);

  const maxDateObj = new Date(today);
  maxDateObj.setDate(maxDateObj.getDate() + 30);

  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const servingsForForm = currentServings ?? mealData.portions;

  // Aktualizuj ref ostatniej poprawnej wartości servings przy inicjalizacji i zmianie currentServings
  useEffect(() => {
    if (servingsForForm >= 0.1 && servingsForForm <= 30) {
      lastValidServingsRef.current = servingsForForm;
    }
  }, [servingsForForm]);

  const mealNameForForm = currentMealName ?? mealName ?? mealData.name;

  const initialValues = useMemo(() => {
    const base = {
      servings: servingsForForm,
      notes: mealData.notes || '',
      saveYourMeal: false,
    };
    const ingredientValues = buildIngredientFormValues(
      ingredients,
      servingsForForm,
      mealNameForForm,
      isEditMode ? false : isBarcode
    );
    if (ingredients.length === 0) {
      return { ...base, mealName: mealNameForForm };
    }
    return {
      ...base,
      ...ingredientValues,
    };
  }, [servingsForForm, ingredients, mealNameForForm, isEditMode, isBarcode]);

  const combinedValidationSchema = useMemo(() => {
    const base = userMealValidationSchema();
    if (isEditMode && ingredients.length === 0) return base;
    const ingredientSchema = createValidationSchema(ingredients);
    return base.shape(ingredientSchema.fields);
  }, [ingredients, isEditMode]);

  const handleSubmit = async (
    values: {
      servings: number;
      notes?: string;
      [key: string]: string | number | boolean | undefined;
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resetForm?: (nextState?: any) => void
  ) => {
    setIsSubmitting(true);

    const ingredientsToSubmit = Object.keys(values).some((k) => k.includes('_unit'))
      ? calculateUpdatedIngredients(
          ingredients,
          values as { [key: string]: number | string },
          values.servings
        )
      : ingredients;

    const requestIngredients = ingredientsToSubmit.map(toRequestIngredient);

    const request = {
      mealId: mealData.id,
      mealName: mealName,
      portions: +values.servings,
      notes: values.notes,
      ingredients: requestIngredients,
    };

    try {
      const response = await editMeal?.(request);

      if (response?.__typename === 'DailyTrackerEditMealSuccess') {
        setShowNotes(false);
        toastSuccess(<p>{t('meals.notification.editMealConfirmation.content')}</p>);

        // Reset form to clear dirty state
        if (resetForm) {
          resetForm({ values });
          setHasIngredientsChanged(false);
        }
      } else {
        toastError();
      }
    } catch (error) {
      toastError();
    } finally {
      setIsSubmitting(false);
    }
  };

  const fakeSubmit = () => {
    inputRef.current?.blur();
  };

  const handleDeleteIngredient = (id: string) => {
    const currentValues = formValuesRef.current as { [key: string]: number | string };
    const servings = getSafeServings(currentValues.servings);
    const hasAmounts = Object.keys(currentValues).some((k) => k.includes('_unit'));
    const synced = hasAmounts
      ? calculateUpdatedIngredients(ingredients, currentValues, servings)
      : ingredients;
    setIngredients(synced.filter((i) => i.id !== id));
    if (isEditMode) {
      setHasIngredientsChanged(true);
    }

    //const deletedIngredient = synced.find((i) => i.id === id);

    // toastSuccess(
    //   <p className="w-100">
    //     {t('tracker.logMeal.ingredients.deleteConfirmation.description')}

    //     <Button
    //       color="link"
    //       className="float-end"
    //       onClick={() => handleAddIngredientBack(deletedIngredient as Ingredient)}
    //     >
    //       {t('tracker.logMeal.ingredients.deleteConfirmation.button')}
    //     </Button>
    //   </p>
    // );
  };

  // const handleAddIngredientBack = (deletedIngredient: Ingredient) => {
  //   setIngredients((prevIngredients) => {
  //     // Prevent adding the same ingredient twice
  //     if (prevIngredients.some((ing) => ing.id === deletedIngredient.id)) {
  //       return prevIngredients;
  //     }
  //     return prevIngredients.concat(deletedIngredient);
  //   });
  //   if (isEditMode) {
  //     setHasIngredientsChanged(true);
  //   }
  // };

  const handleAddIngredientClick = (values: { [key: string]: number | string }) => {
    const updatedIngredients = calculateUpdatedIngredients(
      ingredients,
      values,
      values.servings as number
    );
    navigate('add-ingredient', {
      state: {
        ingredients: updatedIngredients,
        mealName,
        returnPath: location.pathname,
        dietMealId,
        servings: values.servings as number,
      },
    });
  };

  const handleSaveMealNameFromModal = (values: { [key: string]: number | string | null }) => {
    const name = (values.mealName as string) || '';
    setMealName(name);
    if (isEditMode) {
      setHasMealNameChanged(true);
    }
    setShowMealNameModal(false);
  };

  const removeMealHandler = async () => {
    if (!removeMeal) {
      toastError();
      return;
    }
    try {
      setIsRemoving(true);
      const response = await removeMeal(mealData.id);

      if (response?.code === 200) {
        // refresh native tracker data
        callNativeMobileApp({
          action: 'refresh_data',
          payload: {
            module: 'tracker',
            date: date,
          },
        });
        toastSuccess(<p className="w-100">{t('yourMeal.notification.removedFromBase.content')}</p>);
        setHasIngredientsChanged(false);
        navigate(-1);
      } else {
        toastError();
      }
    } catch (error) {
      toastError();
    } finally {
      setIsRemoving(false);
    }
  };

  const noop = useCallback(() => undefined, []);

  /** Returns a valid servings number in [0.1, 30] for display/calculations so invalid input never crashes children. */
  const getSafeServings = useCallback(
    (v: unknown): number => {
      const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
      if (Number.isFinite(n) && n >= 0.1 && n <= 30) return n;
      const fallback = mealData.portions;
      if (Number.isFinite(fallback) && fallback >= 0.1 && fallback <= 30) return fallback;
      return 1;
    },
    [mealData.portions]
  );

  const renderMealImage = () => {
    let mealImg;
    if (mealData.preparationImageUrl) {
      mealImg = mealData.preparationImageUrl;
    } else if (mealData.mealType !== undefined) {
      mealImg = mealPlaceholderImage(mealData.mealType, APP_BRAND as string);
    } else {
      // Fallback for cookbook meals or when mealType is not available
      mealImg = mealData.preparationImageUrl;
    }

    const coverImage = cloudflareImage(mealImg, `${APP_BRAND}`, 'cookbookMealDetails');
    return <img src={coverImage} className="img-fluid" width={768} height={768} alt="" />;
  };

  const handleInstaCart = async () => {
    const ingredients: any = [];

    mealData.ingredients.forEach((ingredient: Ingredient) => {
      const measurement = ingredient.measurements[0];
      const measure = `${measurement.amount} ${measurement.text.replace('%s', '')}`;

      ingredients.push(`${measure} ${ingredient.name}`);
    });

    let mealImg = '';

    if (mealData.preparationImageUrl) {
      mealImg = mealData.preparationImageUrl;
    }

    const request = {
      name: mealName,
      ingredients,
      recipe: [],
      image: mealImg,
      description: `Cookbook recipe`,
    };

    setPreloadInstacart(true);

    const response = await fetch(INSTACART_FIREBASE_FUNCTION.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    response.json().then((data) => {
      setPreloadInstacart(false);

      if (data.instacartUrl) {
        const recipeData = encodeURIComponent(data.instacartUrl);
        const instacartUrl = `${INSTACART_URL}?recipeSourceUrl=${recipeData}&utm_source=instacart_growth_partnerships&aff_id=1818&offer_id=1&affiliate_platform=recipe_widget`;

        trackFirebaseEvent('user_action', {
          action: 'tap-instacart_button',
          location: 'Meal_details',
          meals_tab: 'cookbook',
        });

        if (
          mobileApp &&
          mobileApp.os &&
          mobileApp.os.toLowerCase().startsWith('ios') &&
          mobileApp.version &&
          compareVersions(mobileApp.version, '4.0.2')
        ) {
          callNativeMobileApp({
            action: 'deeplink_open',
            payload: {
              url: instacartUrl,
            },
          });
        } else {
          // workound for typescript error
          const win: Window = window;
          win.location = instacartUrl;
        }
      }
    });
  };

  const handleAddMealToTracker = () => {
    navigate(`/meals/user-meals/module/tracker/add-meal/${mealData.id}`);
  };

  return (
    <>
      <Layout>
        <TitleBar showBackBtn={true}>{t('tracker.mealDetails.navbarTitle')}</TitleBar>

        <Formik
          onSubmit={fakeSubmit}
          initialValues={initialValues}
          validationSchema={combinedValidationSchema}
          enableReinitialize
        >
          {({ isValid, values, setFieldValue, dirty, resetForm }) => {
            formValuesRef.current = values as Record<string, unknown>;
            const formValues = values as Record<string, unknown>;
            const allIngredientFieldsValid = ingredients.every((ing) => {
              const v = formValues[ing.id];
              return v !== null && v !== undefined;
            });
            const isFormValid = isValid && allIngredientFieldsValid;

            // Calculate meal data directly from form values
            const currentServings = getSafeServings(values.servings);
            const hasAmounts = Object.keys(formValues).some((k) => k.includes('_unit'));
            const updatedIngredients = hasAmounts
              ? calculateUpdatedIngredients(
                  ingredients,
                  formValues as { [key: string]: number | string },
                  currentServings
                )
              : ingredients;
            const calculatedMealData = calculateMacrosFromIngredients(updatedIngredients);
            const fiberGrams = calculatedMealData.macro.fiber.grams;

            let showInstacartButton = false;
            if (getCountry() === 'Canada' || getCountry() === 'United States of America') {
              showInstacartButton = true;
            }

            return (
              <Form>
                <div className="page-template-user-meals">
                  <Container className="py-md-4 full-height4">
                    <Row>
                      <Col xl={{ size: 10, offset: 1 }}>
                        <div className="page-container">
                          <div className="meal-details">
                            <Row>
                              <Col md="6">
                                <div className="meal-image-video">
                                  {renderMealImage()}

                                  <div className="meal-info2 d-md-none">
                                    <Container>
                                      <h1 className="meal-title title-large semi-bold white mb-0">
                                        {mealName}
                                      </h1>
                                    </Container>
                                  </div>
                                </div>
                              </Col>
                              <Col md="6">
                                <div className="instacart-container d-md-none">
                                  {showInstacartButton && (
                                    <div className="text-center">
                                      <Button
                                        color="instacart"
                                        className="w-100 w-md-auto"
                                        onClick={handleInstaCart}
                                        disabled={preloadInstacart}
                                      >
                                        {preloadInstacart && (
                                          <span
                                            className="spinner-border spinner-border-sm me-2"
                                            role="status"
                                            aria-hidden="true"
                                          />
                                        )}
                                        {t('orderIngredients.instacart.button')}{' '}
                                        <img
                                          src={instacartLogo}
                                          style={{
                                            verticalAlign: 'baseline',
                                            height: '13px',
                                          }}
                                          alt="instacart"
                                        />
                                      </Button>
                                    </div>
                                  )}
                                </div>

                                <div className="meal-info">
                                  <div className="d-none d-md-block">
                                    <h1 className="meal-title title-large semi-bold mb-xl">
                                      <Button
                                        color="link"
                                        onClick={() => setShowMealNameModal(true)}
                                        className="float-end p-0"
                                      >
                                        <img src={editNeutralIcon} alt="edit" className="ms-1" />
                                      </Button>
                                      {mealName}
                                    </h1>
                                  </div>

                                  <div className="grey-box mb-3">
                                    {t('mealDetails.info.servings.title')}{' '}
                                    <strong className="float-end">{mealData.portions}</strong>
                                  </div>

                                  <Button
                                    color="primary"
                                    size="sm"
                                    className="w-100 d-none d-md-block tracker"
                                    onClick={handleAddMealToTracker}
                                  >
                                    <img
                                      src={addToTrackerIcon}
                                      alt={t('tracker.meal.addToTrackerButton')}
                                      className="me-2"
                                    />
                                    {t('tracker.meal.addToTrackerButton')}
                                  </Button>

                                  <div className="d-none d-md-block">
                                    <div className="table-wrapper">
                                      <Table>
                                        <thead>
                                          <tr>
                                            <th></th>
                                            <th>{t('mealDetails.macrosTable.allServings')}</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          <tr>
                                            <th scope="row">
                                              {t('mealDetails.macros.calories.title')}
                                            </th>
                                            <td>{formatCalories(calculatedMealData.kcal)} kcal</td>
                                          </tr>
                                          <tr>
                                            <th scope="row">
                                              {t('mealDetails.macrosTable.proteins.title')}
                                            </th>

                                            <td>
                                              {formatDecimal(
                                                calculatedMealData.macro.protein.grams
                                              )}{' '}
                                              g
                                            </td>
                                          </tr>
                                          <tr>
                                            <th scope="row">
                                              {t('mealDetails.macrosTable.fats.title')}
                                            </th>
                                            <td>
                                              {formatDecimal(calculatedMealData.macro.fat.grams)} g
                                            </td>
                                          </tr>
                                          <tr>
                                            <th>{t('mealDetails.macrosTable.carbs.title')}</th>
                                            <td>
                                              {formatDecimal(
                                                calculatedMealData.macro.carbohydrates.grams
                                              )}{' '}
                                              g
                                            </td>
                                          </tr>
                                          {Math.round(fiberGrams * 10) > 0 && (
                                            <tr>
                                              <th scope="row">
                                                {t('mealDetails.macrosTable.fiber.title')}
                                              </th>
                                              <td>{formatDecimal(fiberGrams)} g</td>
                                            </tr>
                                          )}
                                        </tbody>
                                      </Table>
                                    </div>
                                  </div>

                                  {showInstacartButton && (
                                    <div className="text-center d-none d-md-block">
                                      <Button
                                        color="instacart"
                                        className="w-100 w-md-auto"
                                        onClick={handleInstaCart}
                                        disabled={preloadInstacart}
                                      >
                                        {preloadInstacart && (
                                          <span
                                            className="spinner-border spinner-border-sm me-2"
                                            role="status"
                                            aria-hidden="true"
                                          />
                                        )}
                                        {t('orderIngredients.instacart.button')}{' '}
                                        <img
                                          src={instacartLogo}
                                          style={{
                                            verticalAlign: 'baseline',
                                            height: '13px',
                                          }}
                                          alt="instacart"
                                        />
                                      </Button>
                                    </div>
                                  )}

                                  {FRISCO && <FriscoButton meal={mealData} publicName={mealName} />}
                                </div>
                              </Col>
                            </Row>
                          </div>
                        </div>

                        <TrackerEditIngredientsForm
                          ingredients={ingredients}
                          multiplier={getSafeServings(values.servings)}
                          values={values as unknown as { [key: string]: number | string | null }}
                          setFieldValue={setFieldValue}
                          onValuesChange={noop}
                          onDeleteIngredient={handleDeleteIngredient}
                          onAddIngredient={() =>
                            handleAddIngredientClick(
                              values as unknown as { [key: string]: number | string }
                            )
                          }
                          decimalSeparator={decimalSeparator}
                          lastIngredientsCount={lastIngredientsCount}
                          setLastIngredientsCount={setLastIngredientsCount}
                          requestId={requestId}
                          showAiSection={
                            Boolean(requestId) || mealData.sourceType === 'ai_description'
                          }
                        />

                        <div className="user-notes">
                          <p className="text-text-main-color semi-bold d-flex justify-content-between w-100">
                            {t('mealDetails.notesBox.title')}

                            {showNotes && (
                              <Button
                                color="link"
                                size="sm"
                                className="p-0"
                                onClick={() => setShowNotes(false)}
                              >
                                {t('mealDetails.notesBox.confirmButton')}
                              </Button>
                            )}

                            {values.notes !== '' && !showNotes && (
                              <Button
                                color="link"
                                size="sm"
                                className="p-0"
                                onClick={() => setShowNotes(true)}
                              >
                                {t('mealDetails.notesBox.editButton')}
                              </Button>
                            )}
                          </p>

                          {showNotes ? (
                            <Input
                              type="textarea"
                              name="notes"
                              className="w-100"
                              innerRef={textareaRef}
                              style={{ resize: 'none', overflow: 'hidden', minHeight: '60px' }}
                            />
                          ) : mealData.notes !== '' || values.notes !== '' ? (
                            <p className="text-text-variant-color text-prewrap">{values.notes}</p>
                          ) : (
                            <button className="add-note w-100" onClick={() => setShowNotes(true)}>
                              <img src={addPlusIcon} alt="add" />
                              <span>{t('mealDetails.notesBox.addButton')}</span>
                            </button>
                          )}
                        </div>

                        <div className="d-md-none page-container">
                          <div className="meal-details">
                            <div className="table-wrapper mx-0">
                              <Table>
                                <thead>
                                  <tr>
                                    <th></th>
                                    <th>{t('mealDetails.macrosTable.allServings')}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr>
                                    <th scope="row">{t('mealDetails.macros.calories.title')}</th>
                                    <td>{formatCalories(calculatedMealData.kcal)} kcal</td>
                                  </tr>
                                  <tr>
                                    <th scope="row">
                                      {t('mealDetails.macrosTable.proteins.title')}
                                    </th>

                                    <td>
                                      {formatDecimal(calculatedMealData.macro.protein.grams)} g
                                    </td>
                                  </tr>
                                  <tr>
                                    <th scope="row">{t('mealDetails.macrosTable.fats.title')}</th>
                                    <td>{formatDecimal(calculatedMealData.macro.fat.grams)} g</td>
                                  </tr>
                                  <tr>
                                    <th>{t('mealDetails.macrosTable.carbs.title')}</th>
                                    <td>
                                      {formatDecimal(calculatedMealData.macro.carbohydrates.grams)}{' '}
                                      g
                                    </td>
                                  </tr>
                                  {Math.round(fiberGrams * 10) > 0 && (
                                    <tr>
                                      <th scope="row">
                                        {t('mealDetails.macrosTable.fiber.title')}
                                      </th>
                                      <td>{formatDecimal(fiberGrams)} g</td>
                                    </tr>
                                  )}
                                </tbody>
                              </Table>
                            </div>
                          </div>
                        </div>

                        <Button
                          color="link"
                          className="w-100 mt-md-3"
                          onClick={removeMealHandler}
                          disabled={isRemoving}
                        >
                          {isRemoving ? (
                            <>
                              <Spinner size="sm" />
                              <span className="ms-2">{t('data-processing')}</span>
                            </>
                          ) : (
                            <>
                              <img src={yourMealsRemoveIcon} alt="delete" className="me-2" />
                              <span className="text-text-error-color">
                                {t('yourMeal.removeButton')}
                              </span>
                            </>
                          )}
                        </Button>
                      </Col>
                    </Row>
                  </Container>

                  <div className="sticky-footer">
                    <Container className="px-3">
                      <Row>
                        <Col xs="12" md={{ size: 4, offset: 4 }}>
                          {dirty || hasIngredientsChanged || hasMealNameChanged ? (
                            <Button
                              type="button"
                              onClick={() => handleSubmit(values, resetForm)}
                              color="primary"
                              className="w-100"
                              disabled={isSubmitting || !isFormValid}
                            >
                              {isSubmitting ? (
                                <>
                                  <Spinner />
                                  <span className="ms-2">{t('data-processing')}</span>
                                </>
                              ) : (
                                t('tracker.mealDetails.saveButton')
                              )}
                            </Button>
                          ) : (
                            <Button
                              color="primary"
                              className="w-100 px-3 tracker"
                              onClick={handleAddMealToTracker}
                            >
                              <img
                                src={addToTrackerIcon}
                                alt={t('tracker.meal.addToTrackerButton')}
                                className="me-2"
                              />
                              {t('tracker.meal.addToTrackerButton')}
                            </Button>
                          )}
                        </Col>
                      </Row>
                    </Container>
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>

        <TrackerMealNameModal
          isOpen={showMealNameModal}
          onClose={() => setShowMealNameModal(false)}
          mealName={mealName}
          onSave={handleSaveMealNameFromModal}
          t={t}
        />
      </Layout>

      <TrackerMealRemoveModal
        isOpen={showAlertModal}
        onToggle={() => setShowAlertModal(!showAlertModal)}
        onConfirm={removeMealHandler}
        t={t}
      />
    </>
  );
};

export default withMobileAppMode(TrackerAddMeal);
